import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge, Pencil, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import type { Olt, Page } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative, formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** One OLT that has been given this profile. */
interface WrittenTo {
  oltId: string;
  oltName: string;
  appliedAt: number;
  appliedBy: string | null;
  /** When a read of that OLT last found it. Null = never seen back. */
  confirmedAt: number | null;
}

interface PushResult {
  oltId: string;
  oltName: string;
  written: boolean;
  /** The OLT had it already, so nothing was sent. Not a failure and not work. */
  alreadyThere: boolean;
  commands: string[];
  error: string | null;
}

interface Profile {
  id: string;
  name: string;
  direction: 'up' | 'down';
  fixedKbps: number | null;
  assuredKbps: number | null;
  maxKbps: number | null;
  notes: string | null;
  createdAt: number;
  createdBy: string | null;
  writtenTo: WrittenTo[];
}

/** kbps as an operator says it: 1048064 -> "1G", 51200 -> "50M". */
function speed(kbps: number | null): string {
  if (kbps === null || kbps <= 0) return '—';
  if (kbps >= 1_000_000) {
    const gig = kbps / 1_048_576;
    return `${gig >= 0.95 && gig <= 1.05 ? 1 : Math.round(gig * 10) / 10}G`;
  }
  return `${Math.round(kbps / 1024)}M`;
}

/** "50M" or "1G" back into kbps, so an operator types what they sell. */
function toKbps(text: string): number | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*([mg])?\s*$/i.exec(text);
  if (!m) return null;
  const value = Number(m[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  const unit = (m[2] ?? 'm').toLowerCase();
  return Math.round(value * (unit === 'g' ? 1_048_576 : 1024));
}

/**
 * The packages this operator sells.
 *
 * A profile is defined here and nowhere else. It reaches a chassis only when a
 * subscriber on that chassis is authorised against it, which keeps each OLT
 * holding just the packages its own subscribers have bought and lets a new OLT
 * join the fleet with no seeding step at all.
 *
 * What the OLTs already held is deliberately absent. Every chassis in this
 * fleet arrived carrying a decade of somebody else's names, and listing those
 * here would turn a page about products into an inventory of what was left
 * behind — each OLT's own Speed profiles tab shows them.
 */
export function SpeedProfilesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const editable = can('olt.manage');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [deleting, setDeleting] = useState<Profile | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const oltsQuery = useQuery({
    queryKey: ['olts'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });
  const olts = oltsQuery.data?.items ?? [];

  const query = useQuery({
    queryKey: ['speed-profiles'],
    queryFn: () => api.get<{ items: Profile[]; oltCount: number }>('/speed-profiles'),
  });
  const items = query.data?.items ?? [];

  /**
   * Ask every OLT what it holds, so "last seen" means something. A profile
   * removed on the CLI stays listed as present until a read fails to find it,
   * and the only way to notice is to look.
   */
  const verify = useMutation({
    mutationFn: async () => {
      await Promise.all(
        olts.map((olt) =>
          api
            .get(`/olts/${encodeURIComponent(olt.id)}/speed-profiles?refresh=1`)
            // One unreachable chassis must not fail the whole check.
            .catch(() => null),
        ),
      );
      await queryClient.invalidateQueries({ queryKey: ['speed-profiles'] });
    },
    onSuccess: () => setNotice('Checked every OLT.'),
  });

  const onNowhere = useMemo(() => items.filter((p) => p.writtenTo.length === 0).length, [items]);
  const [pushing, setPushing] = useState<Profile | null>(null);

  return (
    <>
      <PageHeader
        title="Speed profiles"
        description="The packages you sell. Defined here and written to an OLT the first time a subscriber on it is given one."
        actions={
          editable ? (
            <div className="flex gap-2">
              <Button
                icon={<RefreshCw className="size-4" />}
                loading={verify.isPending}
                onClick={() => verify.mutate()}
                title="Read every OLT and update when each profile was last seen there"
              >
                Check the OLTs
              </Button>
              <Button
                variant="primary"
                icon={<Plus className="size-4" />}
                onClick={() => setCreating(true)}
              >
                New profile
              </Button>
            </div>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Profiles" value={items.length} icon={<Gauge className="size-4" />} />
        <StatCard label="Download" value={items.filter((p) => p.direction === 'down').length} />
        <StatCard label="Upload" value={items.filter((p) => p.direction === 'up').length} />
        <StatCard
          label="Not on any OLT yet"
          value={onNowhere}
          hint={onNowhere > 0 ? 'no subscriber has been given them' : undefined}
        />
      </div>

      {notice && (
        <div className="mt-4 rounded-lg border border-info/30 bg-info-soft px-4 py-2.5 text-sm text-info">
          {notice}
        </div>
      )}

      <Card className="mt-5" title="Your profiles">
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Creating one here writes nothing to a chassis. The first subscriber authorised against a
          profile brings it onto their OLT, and it stays there for the next one.
        </p>

        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : query.error ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Gauge className="size-8" />}
            title="No profiles yet"
            description="Create one for each package you sell — 50M down, 20M up. They reach an OLT when a subscriber there is given one."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Profile</th>
              <th className="th">Direction</th>
              <th className="th">Speed</th>
              <th className="th">On which OLTs</th>
              <th className="th">Last seen there</th>
              <th className="th" />
            </THead>
            <TBody>
              {items.map((p) => {
                // The oldest confirmation across the OLTs holding it, so one
                // chassis that has lost the profile does not read as fresh.
                const seen =
                  p.writtenTo.length === 0 || p.writtenTo.some((w) => w.confirmedAt === null)
                    ? null
                    : Math.min(...p.writtenTo.map((w) => w.confirmedAt!));
                return (
                  <TRow key={p.id}>
                    <td className="td">
                      <span className="font-mono text-xs font-medium text-ink">{p.name}</span>
                      {p.notes && <p className="text-xs text-faint">{p.notes}</p>}
                    </td>
                    <td className="td">
                      <Pill tone={p.direction === 'up' ? 'primary' : 'info'}>
                        {p.direction === 'up' ? 'upload' : 'download'}
                      </Pill>
                    </td>
                    <td className="td font-medium tabular-nums">{speed(p.maxKbps)}</td>
                    <td className="td text-xs text-muted">
                      {p.writtenTo.length === 0 ? (
                        <span className="text-faint">none yet</span>
                      ) : (
                        <>
                          {p.writtenTo.map((w) => w.oltName).join(', ')}
                          {p.writtenTo.length < (query.data?.oltCount ?? 0) && (
                            <span className="ml-1 text-faint">
                              · {(query.data?.oltCount ?? 0) - p.writtenTo.length} not yet
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="td text-xs">
                      {p.writtenTo.length === 0 ? (
                        <span className="text-faint">—</span>
                      ) : seen === null ? (
                        <span
                          className="text-warn"
                          title="Written, but no read of the OLT has found it since"
                        >
                          never
                        </span>
                      ) : (
                        <span className="text-muted" title={formatDateTime(seen, { seconds: true })}>
                          {formatRelative(seen)}
                        </span>
                      )}
                    </td>
                    <td className="td">
                      {editable && (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            icon={<Upload className="size-3.5" />}
                            onClick={() => setPushing(p)}
                            title="Put this package on chosen OLTs now, instead of waiting for the first subscriber on each"
                          >
                            Push
                          </Button>
                          <Button
                            size="sm"
                            icon={<Pencil className="size-3.5" />}
                            onClick={() => setEditing(p)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            icon={<Trash2 className="size-3.5" />}
                            onClick={() => setDeleting(p)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>

      {creating && <ProfileDialog onClose={() => setCreating(false)} />}
      {editing && <ProfileDialog profile={editing} onClose={() => setEditing(null)} />}
      {deleting && <DeleteProfileDialog profile={deleting} onClose={() => setDeleting(null)} />}
      {pushing && (
        <PushProfileDialog
          profile={pushing}
          olts={olts.map((o) => ({ id: o.id, name: o.name }))}
          onClose={() => setPushing(null)}
        />
      )}
    </>
  );
}

/**
 * Create or edit a profile. Nothing here reaches a chassis, so there is no
 * command preview and no OLT to pick — that decision is made per subscriber.
 *
 * A name cannot be changed after creation: it is what identifies the profile on
 * every chassis already holding it, and renaming here would leave those
 * orphaned under the old name with subscribers still bound to it.
 */
/**
 * Put a package on the OLTs that sell it.
 *
 * A profile normally reaches a chassis the first time a subscriber there is
 * given it, which is right for the common case and wrong when an operator is
 * preparing a new package and wants to see it land. Per-OLT rather than
 * everywhere: a package is not always sold on every chassis, and writing all of
 * them to all of them fills each with rate profiles nobody there uses.
 *
 * Shows the commands before sending them, like every other chassis write here.
 */
function PushProfileDialog({
  profile,
  olts,
  onClose,
}: {
  profile: Profile;
  olts: Array<{ id: string; name: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const already = new Set(profile.writtenTo.map((w) => w.oltId));
  // Pre-selects the ones that do not have it, since those are the reason to be
  // here — an operator can still tick a held one to re-check it.
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(olts.filter((o) => !already.has(o.id)).map((o) => o.id)),
  );
  const [plan, setPlan] = useState<PushResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<{ results: PushResult[] }>(
        `/speed-profiles/${encodeURIComponent(profile.id)}/push`,
        { oltIds: [...chosen], dryRun },
      ),
    onSuccess: async (data, dryRun) => {
      setError(null);
      setPlan(data.results);
      if (!dryRun) await queryClient.invalidateQueries({ queryKey: ['speed-profiles'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not push it'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            Push {profile.name} ({profile.direction})
          </h2>
        </header>
        <div className="space-y-4 p-5">
        <p className="text-sm text-muted">
          Writes the package to the OLTs you choose. One that already has it is left alone — the
          chassis is read first, so a profile another system created under the same name is found
          rather than duplicated.
        </p>

        <div className="space-y-1.5">
          {olts.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={chosen.has(o.id)}
                onChange={(e) =>
                  setChosen((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(o.id);
                    else next.delete(o.id);
                    return next;
                  })
                }
              />
              {o.name}
              {already.has(o.id) && <Pill tone="success">already has it</Pill>}
            </label>
          ))}
        </div>

        {plan && (
          <div className="space-y-2 rounded-lg border border-line bg-surface-2 p-3">
            {plan.map((r) => (
              <div key={r.oltId} className="text-xs">
                <span className="font-medium text-ink">{r.oltName}</span>{' '}
                {r.error ? (
                  <span className="text-danger">{r.error}</span>
                ) : r.alreadyThere ? (
                  <span className="text-muted">already has it — nothing sent</span>
                ) : r.written ? (
                  <span className="text-success">written</span>
                ) : (
                  <span className="text-muted">would send {r.commands.length} command(s)</span>
                )}
                {r.commands.length > 0 && (
                  <pre className="mt-1 overflow-x-auto rounded bg-surface p-2 font-mono text-[11px] text-muted">
                    {r.commands.join('\n')}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

          {error && <ErrorNotice error={new Error(error)} />}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            loading={run.isPending && run.variables === true}
            disabled={chosen.size === 0}
            onClick={() => run.mutate(true)}
          >
            Preview
          </Button>
          {/* Nothing is sent until an operator has seen the commands, which is
              how every other chassis write on this platform behaves. The write
              itself is a multi-OLT chassis job, so — like Resync and WAN mode —
              the dialog closes and it runs in the background task tray rather
              than pinning the operator to a spinner and per-OLT results here. */}
          <Button
            variant="primary"
            disabled={chosen.size === 0 || !plan}
            title={plan ? undefined : 'Preview it first'}
            onClick={() => {
              onClose();
              tasks.start({
                label: `Push ${profile.name} to ${chosen.size} OLT(s)`,
                run: () =>
                  api.post<{ results: PushResult[] }>(
                    `/speed-profiles/${encodeURIComponent(profile.id)}/push`,
                    { oltIds: [...chosen], dryRun: false },
                  ),
                done: (d) =>
                  `${d.results.filter((r) => r.written).length} written` +
                  (d.results.some((r) => r.error)
                    ? `, ${d.results.filter((r) => r.error).length} failed`
                    : ''),
                invalidate: [['speed-profiles']],
              });
            }}
          >
            Push to {chosen.size} OLT{chosen.size === 1 ? '' : 's'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function ProfileDialog({ profile, onClose }: { profile?: Profile; onClose: () => void }) {
  const tasks = useTasks();
  const editingExisting = profile !== undefined;
  const [name, setName] = useState(profile?.name ?? '');
  const [direction, setDirection] = useState<'up' | 'down'>(profile?.direction ?? 'down');
  const [rate, setRate] = useState(profile ? speed(profile.maxKbps) : '');
  const [notes, setNotes] = useState(profile?.notes ?? '');

  const kbps = toKbps(rate);
  const nameOk = /^[A-Za-z0-9_.-]+$/.test(name.trim());
  const ready = nameOk && kbps !== null;

  // Only the ceiling. Fixed and assured are guaranteed allocations the chassis
  // rations, not the speed being sold — asking for a 50M assured floor is
  // refused outright, so the API leaves both at each vendor's minimum.
  //
  // Save closes the dialog at once and runs in the tasks tray, like Push
  // below; the API's warnings ride along in the toast.
  const save = () => {
    const shown = name.trim();
    const body = { maxKbps: kbps, notes: notes.trim() || null };
    onClose();
    tasks.start<{ warnings: string[] }>({
      label: `${editingExisting ? 'Save' : 'Create'} speed profile ${shown}`,
      run: async () => {
        if (profile) {
          return api.patch<{ warnings: string[] }>(
            `/speed-profiles/${encodeURIComponent(profile.id)}`,
            body,
          );
        }
        await api.post('/speed-profiles', { ...body, name: shown, direction });
        return { warnings: [] };
      },
      done: (r) =>
        [
          editingExisting ? `"${shown}" updated.` : `"${shown}" created.`,
          ...(r?.warnings ?? []),
        ].join(' '),
      invalidate: [['speed-profiles']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {editingExisting ? `Edit ${profile.name}` : 'New speed profile'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {editingExisting
              ? 'Changes the package here. An OLT already holding it keeps the old rate until you change it there.'
              : 'Saved in SimpleACS. It is written to an OLT the first time a subscriber there is given it.'}
          </p>
        </header>

        <div className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Name"
              hint={
                editingExisting
                  ? 'Fixed — chassis copies are bound to it'
                  : 'Letters, digits, dot, dash and underscore'
              }
              error={name.trim() && !nameOk ? 'Only letters, digits, . - and _' : undefined}
            >
              <Input
                value={name}
                disabled={editingExisting}
                onChange={(e) => setName(e.target.value)}
                placeholder="RESIDENTIAL-50M-DOWN"
                className="font-mono"
              />
            </Field>
            <Field label="Direction" hint={editingExisting ? 'Fixed' : undefined}>
              <select
                className="input"
                value={direction}
                disabled={editingExisting}
                onChange={(e) => setDirection(e.target.value as 'up' | 'down')}
              >
                <option value="down">Download</option>
                <option value="up">Upload</option>
              </select>
            </Field>
          </div>

          <Field
            label="Speed"
            hint={
              kbps === null
                ? 'Type it as you sell it — 50M, 250M, 1G'
                : `${kbps.toLocaleString()} kbps ceiling; guaranteed floors stay at each chassis minimum`
            }
            error={rate.trim() && kbps === null ? 'Try something like 50M or 1G' : undefined}
          >
            <Input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="50M" />
          </Field>

          <Field label="Notes" hint="Optional — what this package is, for whoever comes next">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Home 50/20 package"
            />
          </Field>

          {editingExisting && profile.writtenTo.length > 0 && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {profile.writtenTo.length} OLT
              {profile.writtenTo.length === 1 ? '' : 's'} already hold this profile at its current
              rate. Changing it here does not change them.
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={save}>
            {editingExisting ? 'Save' : 'Create'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Removes the package. Chassis copies stay: subscribers are bound to them by
 * name, and pulling one out from under a live service would drop that
 * subscriber's shaping without anybody choosing to.
 */
function DeleteProfileDialog({ profile, onClose }: { profile: Profile; onClose: () => void }) {
  const tasks = useTasks();
  const call = () => {
    onClose();
    tasks.start<{ warnings: string[] }>({
      label: `Delete speed profile ${profile.name}`,
      run: () =>
        api.delete<{ warnings: string[] }>(`/speed-profiles/${encodeURIComponent(profile.id)}`),
      done: (r) => [`"${profile.name}" deleted.`, ...(r?.warnings ?? [])].join(' '),
      invalidate: [['speed-profiles']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Delete {profile.name}</h2>
          <p className="mt-0.5 text-xs text-muted">
            Removes the package from SimpleACS so it can no longer be assigned.
          </p>
        </header>
        <div className="space-y-3 p-5">
          {profile.writtenTo.length > 0 && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {profile.writtenTo.map((w) => w.oltName).join(', ')} already hold this profile and
              keep it — subscribers there are bound to it by name. Remove it from each OLT's own
              Speed profiles tab if it should be gone from the chassis too.
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={call}>
            Delete
          </Button>
        </footer>
      </div>
    </div>
  );
}
